<script setup lang="ts">
import { BModal, useModal } from 'bootstrap-vue-next';
import ButtonPrimary from '@buttons/button-primary.vue';
import FormCheckbox from "@form/form-checkbox.vue";
import { computed, ref } from 'vue';
import {useI18n} from 'vue-i18n';
import {useUserApi} from "@store/api/userApi";
import {useUserStore} from "@/store/user";
import {useToast} from "vue-toast-notification";
import { useCommonStore } from '@/store/common';

const toast = useToast();
const {t} = useI18n();
const emit = defineEmits(['accept']);
const props = defineProps({
	id: {
		type: String,
		default: 'modal-privacy-policy'
	}
});
const commonStore = useCommonStore();
const userStore = useUserStore();
const userApi = useUserApi();
const { hide } = useModal(props.id);

const loading = ref(false);
const policyForm = ref({
	accept: false
});

const submitHandler = async() => {
	try {
		if (loading.value) {
			return false;
		}

		loading.value = true;
		const user = await userApi.setPolicy();
		userStore.methods.refresh(user);

		emit('accept', policyForm.value);
		hide();
	} catch (error) {
		console.error('PolicyError', error);
		toast.error(t('common.toast.accept_policy_error'), {
			position: 'top'
		});
	} finally {
		loading.value = false;
	}
};

const fields = computed(() => ({
	title: t('policyModal.title'),
	description: t('policyModal.description'),
	button: t('policyModal.button'),
	policy: t('policyModal.policy', {
		terms_url: commonStore.links.terms
	}),
}));
</script>

<template>
	<BModal
		:id="id"
		no-footer
		no-trap
		no-header-close
		no-close-on-backdrop
		centered
		class="modal-privacy-policy"
	>
		<template #header>
			<div class="modal-privacy-policy__header">
				<img
					src="/images/modal-logo.svg"
					alt=""
					class="modal-privacy-policy__header-logo"
				>
			</div>
		</template>

		<form
			@submit.prevent="submitHandler"
			class="modal-privacy-policy__form"
		>
			<div v-if="fields?.title" class="modal-privacy-policy__title font-24-pro-bold">{{ fields.title }}</div>
			<div v-if="fields?.description" class="modal-privacy-policy__description">{{ fields.description }}</div>
			<button-primary
				v-if="fields?.button"
				:loading="loading"
				:disabled="!policyForm.accept"
				:icon="{name: 'arrowRight', viewBox: '0 0 40 40'}"
				class="modal-privacy-policy__button"
			>
				{{ fields.button }}
			</button-primary>

			<form-checkbox
				v-if="fields?.policy"
				name="accept"
				class="modal-privacy-policy__checkbox"
				v-model="policyForm.accept"
			>
				<span v-html="fields.policy" />
			</form-checkbox>
		</form>
	</BModal>
</template>

<style lang="scss">
.modal-privacy-policy {
	@include gradient-modal;

	&__header {
		width: 100%;
		height: 28rem;
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;

		@include min-desktop {
			height: 36.8rem;
		}
	}

	&__header-logo {
		width: 14rem;
		@include min-desktop {
			width: 20rem;
		}
	}
	&__title {
		margin-bottom: 1.6rem;
		@include min-desktop {
			@include font-32-pro-bold;
		}
	}
	&__description {
		color: rgba(var(--color-black-rgb), 0.5);
		@include min-desktop {
			@include font-16-pro-regular;
		}
	}
	&__checkbox {
		margin-top: 3.2rem;
		color: rgba(var(--color-black-rgb), 0.5);
	}
	&__button {
		margin-top: 3.2rem;
		width: 100%;
		@include min-desktop {
			height: 6.2rem;
		}
	}
	&__close {
		position: absolute;
		top: 1.6rem;
		right: 1.6rem;
	}
}
</style>
